import { Grid, TextField, Box, Autocomplete, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getAllInputs, getAllRolls } from "../functions";
import { getAllTelas } from "components-base/Inputs/functions";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";

const StackTelasFormFields = ({ formik, setFieldValue, mode }) => {
  const [telasData, setTelasData] = useState([]);
  const [inputsData, setInputsData] = useState([]);
  const [rollsData, setRollsData] = useState([]);

  function getNextCodeFixed(data) {
    const prefix = "TELA";
    const codes = data
      .map((item) => item.code)
      .filter((code) => code.startsWith(prefix));

    if (codes.length === 0) return prefix + "000001";

    const sorted = codes.sort((a, b) => {
      const numA = parseInt(a.slice(prefix.length), 10);
      const numB = parseInt(b.slice(prefix.length), 10);
      return numA - numB;
    });

    const lastNum = parseInt(sorted[sorted.length - 1].slice(prefix.length), 10);
    let nextNumStr = (lastNum + 1).toString().padStart(6, "0");
    if (nextNumStr.length > 6) nextNumStr = nextNumStr.slice(nextNumStr.length - 6);
    return prefix + nextNumStr;
  }

  useEffect(() => {
    getAllTelas().then((d) => setTelasData(d || []));
    getAllInputs().then((d) => setInputsData(d || []));
    getAllRolls().then((d) => setRollsData(d || []));
  }, []);

  useEffect(() => {
    if (rollsData.length > 0 && !formik.values.code && mode !== "PUT") {
      formik.setFieldValue("code", getNextCodeFixed(rollsData));
    }
  }, [rollsData, formik.values.code, mode]);

  const dataFilterInputs = inputsData.filter(
    (item) => item?.category?.name === "Telas"
  );

  return (
    <Box
      sx={{
        p: 3,
        background: "#fafafa",
        ".dark &": { background: "#0f172a" },
        "& .MuiOutlinedInput-root": {
          fontFamily: "'Poppins', sans-serif",
          fontSize: "0.9rem",
          borderRadius: 2,
          "&.Mui-focused fieldset": { borderColor: "#f43f5e" },
        },
        "& .MuiInputLabel-root": { fontFamily: "'Poppins', sans-serif" },
        "& .MuiInputLabel-root.Mui-focused": { color: "#f43f5e" },
      }}
    >
      {/* === Información General === */}
      <Box
        sx={{
          p: 3, mb: 2, borderRadius: 2,
          border: "1px solid rgba(244,63,94,0.15)",
          background: "#fff",
          ".dark &": { background: "#1e293b", borderColor: "rgba(244,63,94,0.2)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <AssignmentIcon sx={{ color: "#f43f5e", fontSize: 20 }} />
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif", fontWeight: 700,
              fontSize: "0.85rem", textTransform: "uppercase",
              letterSpacing: "0.07em", color: "#be185d",
            }}
          >
            Información General
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              id="cloth"
              name="cloth"
              size="small"
              fullWidth
              options={telasData}
              getOptionLabel={(option) => `${option.name}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => setFieldValue("cloth", value)}
              value={formik.values.cloth}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>{option.name}</li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tipo de Tela"
                  color="primary"
                  error={formik.touched.cloth && Boolean(formik.errors.cloth)}
                  helperText={formik.touched.cloth && formik.errors.cloth}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              id="code"
              name="code"
              size="small"
              label="Código"
              value={formik.values.code}
              onChange={formik.handleChange}
              color="primary"
              disabled
              error={formik.touched.code && Boolean(formik.errors.code)}
              helperText={formik.touched.code && formik.errors.code}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              id="color"
              name="color"
              size="small"
              label="Color"
              value={formik.values.color}
              onChange={formik.handleChange}
              color="primary"
              error={formik.touched.color && Boolean(formik.errors.color)}
              helperText={formik.touched.color && formik.errors.color}
            />
          </Grid>
        </Grid>
      </Box>

      {/* === Detalle === */}
      <Box
        sx={{
          p: 3, mb: 2, borderRadius: 2,
          border: "1px solid rgba(244,63,94,0.15)",
          background: "#fff",
          ".dark &": { background: "#1e293b", borderColor: "rgba(244,63,94,0.2)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <DescriptionIcon sx={{ color: "#f43f5e", fontSize: 20 }} />
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif", fontWeight: 700,
              fontSize: "0.85rem", textTransform: "uppercase",
              letterSpacing: "0.07em", color: "#be185d",
            }}
          >
            Detalle
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              id="inputs"
              name="inputs"
              size="small"
              fullWidth
              options={dataFilterInputs}
              getOptionLabel={(option) => `${option.code} — ${option.date}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => setFieldValue("inputs", value)}
              value={formik.values.inputs}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>{`${option.code} — ${option.date}`}</li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Entrada del Rollo"
                  color="primary"
                  error={formik.touched.inputs && Boolean(formik.errors.inputs)}
                  helperText={formik.touched.inputs && formik.errors.inputs}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              id="totalKg"
              name="totalKg"
              size="small"
              label="Total de Kg"
              type="number"
              value={formik.values.totalKg}
              onChange={formik.handleChange}
              color="primary"
              inputProps={{ min: 0, step: "any" }}
              error={formik.touched.totalKg && Boolean(formik.errors.totalKg)}
              helperText={formik.touched.totalKg && formik.errors.totalKg}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              id="meters"
              name="meters"
              size="small"
              label="Metros"
              type="number"
              value={formik.values.meters}
              onChange={formik.handleChange}
              color="primary"
              inputProps={{ min: 0, step: "any" }}
              error={formik.touched.meters && Boolean(formik.errors.meters)}
              helperText={formik.touched.meters && formik.errors.meters}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default StackTelasFormFields;
